import React, { useState, useEffect, useCallback } from "react";
import {
  getUsuariosContexto, getUsuario, getListasApoyoUsuarios, buscarUsuarios,
  crearUsuario, actualizarUsuario, eliminarUsuarioDefinitivo,
  asignarEmpresaAUsuario, actualizarRolEmpresaUsuario, toggleActivoEmpresaUsuario, quitarEmpresaUsuario,
  asignarGrupoAUsuario, actualizarRolGrupoUsuario, toggleActivoGrupoUsuario, quitarGrupoUsuario,
  UsuarioContextoListDto, UsuarioDetailDto, ListasApoyo, UsuarioBusquedaDto
} from "../../services/api";

interface Props {
  token: string;
  usuarioId: number;
  modo: "grupo" | "empresa";
  grupoActivoId: number | null;
  empresaActivaId: number | null;
}

type Estado = "todos" | "activos" | "inactivos";
type ModoPanel = "nueva" | "editarSuper" | "empresasDeGrupo" | "agregarExistente" | null;

export default function Usuarios({ token, usuarioId, modo, grupoActivoId, empresaActivaId }: Props) {
  const [cargando, setCargando] = useState(true);
  const [alcance, setAlcance] = useState<string>("");
  const [scopeGrupoId, setScopeGrupoId] = useState<number | null>(null);
  const [scopeEmpresaId, setScopeEmpresaId] = useState<number | null>(null);

  const [usuarios, setUsuarios] = useState<UsuarioContextoListDto[]>([]);
  const [estado, setEstado] = useState<Estado>("activos");
  const [busqueda, setBusqueda] = useState("");
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<UsuarioContextoListDto | null>(null);
  const [detalle, setDetalle] = useState<UsuarioDetailDto | null>(null);
  const [modoPanel, setModoPanel] = useState<ModoPanel>(null);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [listasApoyo, setListasApoyo] = useState<ListasApoyo>({ empresas: [], grupos: [] });
  const [nuevaEmpresa, setNuevaEmpresa] = useState({ empresaId: 0, rol: "Usuario", activo: false });

  const [formNuevo, setFormNuevo] = useState({
    email: "", nombre: "", password: "", activo: true, esSuperUsuario: false,
  });

  // ----- Busqueda y alta de usuario EXISTENTE (exclusivo Superusuario) -----
  const [busquedaExistente, setBusquedaExistente] = useState("");
  const [resultadosBusqueda, setResultadosBusqueda] = useState<UsuarioBusquedaDto[]>([]);
  const [buscandoExistente, setBuscandoExistente] = useState(false);
  const [asignacionForm, setAsignacionForm] = useState<{ [usuarioId: number]: { rol: string; activo: boolean } }>({});

  const cargar = useCallback(async () => {
    const opts = modo === "grupo"
      ? { grupoId: grupoActivoId, estado, busqueda: busqueda || undefined }
      : { empresaId: empresaActivaId, estado, busqueda: busqueda || undefined };

    const resp = await getUsuariosContexto(usuarioId, opts, token);
    if (resp) {
      setAlcance(resp.alcance);
      setUsuarios(resp.usuarios);
      setScopeGrupoId(resp.grupoEmpresaId);
      setScopeEmpresaId(resp.empresaId);
    } else {
      setAlcance("");
      setUsuarios([]);
    }
    setCargando(false);
  }, [usuarioId, modo, grupoActivoId, empresaActivaId, estado, busqueda, token]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    getListasApoyoUsuarios(token, usuarioId, grupoActivoId).then(setListasApoyo);
  }, [token, usuarioId, grupoActivoId]);

  const cerrarPanel = () => {
    setModoPanel(null);
    setDetalle(null);
    setUsuarioSeleccionado(null);
    setMensajeOk("");
    setMensajeError("");
    setBusquedaExistente("");
    setResultadosBusqueda([]);
  };

  // ----- Superusuario: alta de usuario nuevo -----

  const handleNuevo = () => {
    setFormNuevo({ email: "", nombre: "", password: "", activo: true, esSuperUsuario: false });
    setModoPanel("nueva");
    setMensajeOk("");
    setMensajeError("");
  };

  const handleCrear = async () => {
    if (!formNuevo.email || !formNuevo.nombre || !formNuevo.password) {
      setMensajeError("Email, nombre y contraseña son obligatorios");
      return;
    }
    setGuardando(true);
    const resultado = await crearUsuario({ ...formNuevo, grupoEmpresaId: grupoActivoId }, token);
    if (resultado.ok) {
      setMensajeOk("Usuario creado. Queda inactivo en el grupo hasta que el GroupAdmin lo active.");
      await cargar();
      setTimeout(cerrarPanel, 1800);
    } else {
      setMensajeError(resultado.mensaje || "Error al crear el usuario");
    }
    setGuardando(false);
  };

  // ----- Superusuario: editar datos + grupos -----

  const handleEditarSuper = async (u: UsuarioContextoListDto) => {
    setUsuarioSeleccionado(u);
    setModoPanel("editarSuper");
    setMensajeOk("");
    setMensajeError("");
    const d = await getUsuario(u.usuarioId, token);
    setDetalle(d);
  };

  const recargarDetalleSuper = async (uid: number) => {
    const d = await getUsuario(uid, token);
    setDetalle(d);
  };

  const handleEliminarUsuario = async () => {
    if (!detalle) return;
    if (detalle.usuarioId === usuarioId) {
      setMensajeError("No podés eliminar tu propio usuario.");
      return;
    }
    if (!window.confirm(`¿Eliminar definitivamente a ${detalle.nombre}? Esta acción no se puede deshacer.`)) return;
    setGuardando(true);
    const res = await eliminarUsuarioDefinitivo(detalle.usuarioId, usuarioId, token);
    setGuardando(false);
    if (res.ok) {
      await cargar();
      cerrarPanel();
    } else {
      setMensajeError(res.mensaje || "Error al eliminar");
    }
  };

  const handleGuardarDatosSuper = async () => {
    if (!detalle) return;
    setGuardando(true);
    const ok = await actualizarUsuario(detalle.usuarioId, {
      nombre: detalle.nombre, activo: detalle.activo, esSuperUsuario: detalle.esSuperUsuario,
    }, token);
    if (ok) {
      setMensajeOk("Guardado");
      await cargar();
    } else {
      setMensajeError("Error al guardar");
    }
    setGuardando(false);
  };

  const gruposDisponiblesParaAsignar = listasApoyo.grupos.filter(
    g => !detalle?.grupos.some(dg => dg.grupoEmpresaId === g.grupoEmpresaId)
  );
  const [nuevoGrupoAsignar, setNuevoGrupoAsignar] = useState({ grupoEmpresaId: 0, rol: "User", activo: false });

  const handleAgregarGrupo = async () => {
    if (!detalle || !nuevoGrupoAsignar.grupoEmpresaId) return;
    const ok = await asignarGrupoAUsuario(
      detalle.usuarioId, nuevoGrupoAsignar.grupoEmpresaId, nuevoGrupoAsignar.rol, nuevoGrupoAsignar.activo, token
    );
    if (ok) {
      await recargarDetalleSuper(detalle.usuarioId);
      setNuevoGrupoAsignar({ grupoEmpresaId: 0, rol: "User", activo: false });
    } else {
      setMensajeError("Error al asignar el grupo");
    }
  };

  const handleQuitarGrupo = async (grupoEmpresaId: number) => {
    if (!detalle) return;
    if (!window.confirm(
      "Esto va a quitar al usuario del grupo y desactivar todas las empresas activas que tenga dentro de él. ¿Confirmás?"
    )) return;

    const r = await quitarGrupoUsuario(detalle.usuarioId, grupoEmpresaId, token);
    if (!r.ok) setMensajeError(r.mensaje || "No se pudo quitar");
    await recargarDetalleSuper(detalle.usuarioId);
  };

  // ----- Activar/desactivar membresía de grupo (vista de grupo) -----

  const handleToggleGrupo = async (u: UsuarioContextoListDto) => {
    if (!scopeGrupoId) return;

    if (u.membresiaActiva && !window.confirm(
      `Esto va a desactivar a ${u.nombre} en este grupo y en todas las empresas activas que tenga dentro de él. ¿Confirmás?`
    )) return;

    setMensajeError("");
    const r = await toggleActivoGrupoUsuario(u.usuarioId, scopeGrupoId, !u.membresiaActiva, token);
    if (!r.ok) setMensajeError(r.mensaje || "No se pudo cambiar el estado");
    await cargar();
  };

  const handleCambiarRolGrupo = async (u: UsuarioContextoListDto, nuevoRol: string) => {
    if (!scopeGrupoId) return;
    setMensajeError("");
    const r = await actualizarRolGrupoUsuario(u.usuarioId, scopeGrupoId, nuevoRol, token);
    if (!r.ok) setMensajeError(r.mensaje || "No se pudo cambiar el rol");
    await cargar();
  };

  // ----- Activar/desactivar por empresa (vista de empresa) -----

  const handleToggleEmpresaDirecto = async (u: UsuarioContextoListDto) => {
    if (!scopeEmpresaId) return;
    setMensajeError("");
    const r = await toggleActivoEmpresaUsuario(u.usuarioId, scopeEmpresaId, !u.membresiaActiva, token);
    if (!r.ok) setMensajeError(r.mensaje || "No se pudo cambiar el estado");
    await cargar();
  };

  const handleCambiarRolEmpresaDirecto = async (u: UsuarioContextoListDto, nuevoRol: string) => {
    if (!scopeEmpresaId) return;
    setMensajeError("");
    await actualizarRolEmpresaUsuario(u.usuarioId, scopeEmpresaId, nuevoRol, token);
    await cargar();
  };

  // ----- Panel "Empresas" del usuario (vista de grupo) -----

  const handleAbrirEmpresasDeGrupo = async (u: UsuarioContextoListDto) => {
    setUsuarioSeleccionado(u);
    setModoPanel("empresasDeGrupo");
    setMensajeOk("");
    setMensajeError("");
    setNuevaEmpresa({ empresaId: 0, rol: "Usuario", activo: false });
    const d = await getUsuario(u.usuarioId, token);
    setDetalle(d);
  };

  const empresasDelGrupoAsignadas = detalle?.empresas.filter(e =>
    listasApoyo.empresas.some(le => le.empresaId === e.empresaId)
  ) || [];
  const empresasDelGrupoDisponibles = listasApoyo.empresas.filter(
    e => !detalle?.empresas.some(de => de.empresaId === e.empresaId)
  );

  const handleAgregarEmpresa = async () => {
    if (!detalle || !nuevaEmpresa.empresaId) return;
    const ok = await asignarEmpresaAUsuario(detalle.usuarioId, nuevaEmpresa.empresaId, nuevaEmpresa.rol, nuevaEmpresa.activo, token);
    if (ok) {
      const d = await getUsuario(detalle.usuarioId, token);
      setDetalle(d);
      setNuevaEmpresa({ empresaId: 0, rol: "Usuario", activo: false });
    } else {
      setMensajeError("Error al asignar la empresa");
    }
  };

  const handleCambiarRolEmpresaEnPanel = async (empresaId: number, rol: string) => {
    if (!detalle) return;
    await actualizarRolEmpresaUsuario(detalle.usuarioId, empresaId, rol, token);
    const d = await getUsuario(detalle.usuarioId, token);
    setDetalle(d);
  };

  const handleToggleEmpresaEnPanel = async (empresaId: number, activo: boolean, nombreEmpresa: string) => {
    if (!detalle) return;
    if (!activo && !window.confirm(`¿Desactivar a ${detalle.nombre} en ${nombreEmpresa}?`)) return;
    await toggleActivoEmpresaUsuario(detalle.usuarioId, empresaId, activo, token);
    const d = await getUsuario(detalle.usuarioId, token);
    setDetalle(d);
  };

  const handleQuitarEmpresa = async (empresaId: number) => {
    if (!detalle) return;
    await quitarEmpresaUsuario(detalle.usuarioId, empresaId, token);
    const d = await getUsuario(detalle.usuarioId, token);
    setDetalle(d);
  };

  // ----- Buscar y agregar usuario EXISTENTE (exclusivo Superusuario, en grupo o empresa) -----

  const handleAbrirAgregarExistente = () => {
    setModoPanel("agregarExistente");
    setBusquedaExistente("");
    setResultadosBusqueda([]);
    setAsignacionForm({});
    setMensajeOk("");
    setMensajeError("");
  };

  const handleBuscarExistente = async () => {
    if (busquedaExistente.trim().length < 2) return;
    setBuscandoExistente(true);
    const r = await buscarUsuarios(busquedaExistente.trim(), token);
    setResultadosBusqueda(r.filter(u => !usuarios.some(existing => existing.usuarioId === u.usuarioId)));
    setBuscandoExistente(false);
  };

  const handleAgregarExistente = async (uid: number) => {
    const rolDefault = modo === "grupo" ? "User" : "Usuario";
    const cfg = asignacionForm[uid] || { rol: rolDefault, activo: false };

    let ok = false;
    if (modo === "grupo" && scopeGrupoId) {
      ok = await asignarGrupoAUsuario(uid, scopeGrupoId, cfg.rol, cfg.activo, token);
    } else if (modo === "empresa" && scopeEmpresaId) {
      ok = await asignarEmpresaAUsuario(uid, scopeEmpresaId, cfg.rol, cfg.activo, token);
    }

    if (ok) {
      setMensajeOk(modo === "grupo" ? "Usuario agregado al grupo." : "Usuario agregado a la empresa.");
      setResultadosBusqueda(prev => prev.filter(u => u.usuarioId !== uid));
      await cargar();
    } else {
      setMensajeError("No se pudo agregar el usuario");
    }
  };

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Cargando usuarios...</div>;

  const titulo = modo === "grupo" ? "Usuarios del grupo" : "Usuarios de la empresa";

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ ...styles.container, flex: 1 }}>
        <div style={styles.toolbar}>
          <span style={styles.titulo}>{titulo}</span>
          <div style={{ display: "flex", gap: 8 }}>
            {alcance === "Superusuario" && (
              <>
                <button style={styles.btnSecundario} onClick={handleAbrirAgregarExistente}>
                  + Agregar usuario existente
                </button>
                <button style={styles.btnNuevo} onClick={handleNuevo}>+ Nuevo usuario</button>
              </>
            )}
          </div>
        </div>

        <div style={styles.filtros}>
          <div style={styles.segmented}>
            {(["activos", "inactivos", "todos"] as Estado[]).map(e => (
              <button
                key={e}
                style={{ ...styles.segItem, ...(estado === e ? styles.segItemActivo : {}) }}
                onClick={() => setEstado(e)}
              >
                {e === "activos" ? "Activos" : e === "inactivos" ? "Inactivos" : "Todos"}
              </button>
            ))}
          </div>
          <input
            style={styles.buscador}
            placeholder="Buscar por nombre o email..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        {mensajeError && <div style={styles.mensajeErrorGlobal}>✕ {mensajeError}</div>}
        {!cargando && !alcance && (
          <div style={styles.mensajeErrorGlobal}>No tenés permisos de administración en este contexto.</div>
        )}

        <div style={styles.encabezado}>
          <div style={{ ...styles.colH, flex: 1 }}>Email</div>
          <div style={{ ...styles.colH, flex: 1 }}>Nombre</div>
          <div style={{ ...styles.colH, width: 120, textAlign: "center" }}>
            {modo === "grupo" ? "Rol en el grupo" : "Rol en la empresa"}
          </div>
          <div style={{ ...styles.colH, width: 90, textAlign: "center" }}>Activo</div>
          <div style={{ ...styles.colH, width: 140, textAlign: "center" }}>Acciones</div>
        </div>
        <div style={styles.tabla}>
          {usuarios.length === 0 && (
            <div style={styles.vacio}>No hay usuarios para este filtro</div>
          )}
          {usuarios.map(u => (
            <div key={u.usuarioId} style={{ ...styles.fila, opacity: u.membresiaActiva ? 1 : 0.55 }}>
              <div style={{ ...styles.col, flex: 1 }}>{u.email}</div>
              <div style={{ ...styles.col, flex: 1 }}>
                {u.nombre} {u.esSuperUsuario && <span style={styles.badgeSuper}>Superusuario</span>}
              </div>
              <div style={{ ...styles.col, width: 120, justifyContent: "center" }}>
                {!u.esSuperUsuario && alcance === "EmpresaAdmin" ? (
                  <select
                    style={styles.miniSelect}
                    value={u.rolEnContexto || "Usuario"}
                    onChange={e => handleCambiarRolEmpresaDirecto(u, e.target.value)}
                  >
                    <option value="Usuario">Usuario de la empresa</option>
                    <option value="EmpresaAdmin">Admin de la empresa</option>
                  </select>
                ) : !u.esSuperUsuario && alcance === "GroupAdmin" ? (
                  <select
                    style={styles.miniSelect}
                    value={u.rolEnContexto || "User"}
                    onChange={e => handleCambiarRolGrupo(u, e.target.value)}
                  >
                    <option value="User">Usuario del grupo</option>
                    <option value="GroupAdmin">Admin del grupo</option>
                  </select>
                ) : (
                  <span style={{ color: "#888" }}>{u.rolEnContexto || "—"}</span>
                )}
              </div>
              <div style={{ ...styles.col, width: 90, justifyContent: "center" }}>
                {u.membresiaActiva ? "✓" : "—"}
              </div>
              <div style={{ ...styles.col, width: 140, justifyContent: "center", gap: 6 }}>
                {alcance === "Superusuario" && (
                  <button style={styles.btnAccion} onClick={() => handleEditarSuper(u)}>Editar</button>
                )}
                {alcance === "EmpresaAdmin" && (
                  <button style={styles.btnAccion} onClick={() => handleToggleEmpresaDirecto(u)}>
                    {u.membresiaActiva ? "Desactivar" : "Activar"}
                  </button>
                )}
                {alcance === "GroupAdmin" && (
                  <>
                    <button style={styles.btnAccion} onClick={() => handleToggleGrupo(u)}>
                      {u.membresiaActiva ? "Desactivar" : "Activar"}
                    </button>
                    <button style={styles.btnAccion} onClick={() => handleAbrirEmpresasDeGrupo(u)}>Empresas</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PANEL: nuevo usuario (Superusuario) */}
      {modoPanel === "nueva" && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Nuevo Usuario</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Email</label>
            <input style={styles.campoInput} value={formNuevo.email}
              onChange={e => setFormNuevo({ ...formNuevo, email: e.target.value })} />
          </div>
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Nombre</label>
            <input style={styles.campoInput} value={formNuevo.nombre}
              onChange={e => setFormNuevo({ ...formNuevo, nombre: e.target.value })} />
          </div>
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Contraseña</label>
            <input style={styles.campoInput} type="password" value={formNuevo.password}
              onChange={e => setFormNuevo({ ...formNuevo, password: e.target.value })} />
          </div>
          <div style={styles.campoCheckbox}>
            <input type="checkbox" checked={formNuevo.esSuperUsuario}
              onChange={e => setFormNuevo({ ...formNuevo, esSuperUsuario: e.target.checked })} />
            <span>Superusuario</span>
          </div>
          {grupoActivoId && (
            <p style={styles.nota}>
              Se va a agregar al grupo actual, inactivo — el GroupAdmin del grupo lo activa después.
            </p>
          )}
          {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button style={styles.btnGuardar} onClick={handleCrear} disabled={guardando}>
              {guardando ? "Creando..." : "Crear"}
            </button>
            <button style={styles.btnCancelar} onClick={cerrarPanel}>Cancelar</button>
          </div>
        </div>
      )}

      {/* PANEL: editar (Superusuario) — Datos + Grupos */}
      {modoPanel === "editarSuper" && detalle && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Editar Usuario</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          <h4 style={styles.subTitulo}>Datos</h4>
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Nombre</label>
            <input style={styles.campoInput} value={detalle.nombre}
              onChange={e => setDetalle({ ...detalle, nombre: e.target.value })} />
          </div>
          <div style={styles.campoCheckbox}>
            <input type="checkbox" checked={detalle.activo}
              onChange={e => setDetalle({ ...detalle, activo: e.target.checked })} />
            <span>Cuenta activa</span>
          </div>
          <div style={styles.campoCheckbox}>
            <input type="checkbox" checked={detalle.esSuperUsuario}
              onChange={e => setDetalle({ ...detalle, esSuperUsuario: e.target.checked })} />
            <span>Superusuario</span>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button style={{ ...styles.btnGuardar, marginBottom: 0, flex: 1 }} onClick={handleGuardarDatosSuper} disabled={guardando}>
              {guardando ? "Guardando..." : "Guardar datos"}
            </button>
            <button style={styles.btnEliminar} onClick={handleEliminarUsuario} disabled={guardando}>
              Eliminar
            </button>
          </div>

          <h4 style={styles.subTitulo}>Grupos</h4>
          {gruposDisponiblesParaAsignar.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <select style={{ ...styles.campoInput, flex: 1, minWidth: 120 }}
                value={nuevoGrupoAsignar.grupoEmpresaId}
                onChange={e => setNuevoGrupoAsignar({ ...nuevoGrupoAsignar, grupoEmpresaId: Number(e.target.value) })}>
                <option value={0}>— Elegir grupo —</option>
                {gruposDisponiblesParaAsignar.map(g => (
                  <option key={g.grupoEmpresaId} value={g.grupoEmpresaId}>{g.nombre}</option>
                ))}
              </select>
              <select style={{ ...styles.campoInput, width: 110 }}
                value={nuevoGrupoAsignar.rol}
                onChange={e => setNuevoGrupoAsignar({ ...nuevoGrupoAsignar, rol: e.target.value })}>
                <option value="User">User</option>
                <option value="GroupAdmin">GroupAdmin</option>
              </select>
              <label style={styles.checkboxInline}>
                <input type="checkbox" checked={nuevoGrupoAsignar.activo}
                  onChange={e => setNuevoGrupoAsignar({ ...nuevoGrupoAsignar, activo: e.target.checked })} />
                Activo
              </label>
              <button style={styles.btnNuevo} onClick={handleAgregarGrupo}>+</button>
            </div>
          )}
          {detalle.grupos.length === 0 && <div style={styles.vacio}>Sin grupos asignados</div>}
          {detalle.grupos.map(g => (
            <div key={g.grupoEmpresaId} style={styles.filaAsignacion}>
              <span style={{ flex: 1 }}>{g.nombre}</span>
              <span style={{ fontSize: 12, color: g.activo ? "#27ae60" : "#aaa" }}>
                {g.rol} · {g.activo ? "activo" : "inactivo"}
              </span>
              <button style={{ ...styles.btnAccion, color: "#e74c3c", border: "1px solid #e74c3c" }}
                onClick={() => handleQuitarGrupo(g.grupoEmpresaId)}>✕</button>
            </div>
          ))}

          <h4 style={styles.subTitulo}>Empresas</h4>
          {detalle.empresas.length === 0 && <div style={styles.vacio}>Sin empresas asignadas</div>}
          {detalle.empresas.map(e => (
            <div key={e.empresaId} style={styles.filaAsignacion}>
              <span style={{ flex: 1, fontSize: 13 }}>{e.nombre}</span>
              <select
                style={{ ...styles.campoInput, width: 160 }}
                value={e.rol}
                onChange={async ev => {
                  await actualizarRolEmpresaUsuario(detalle.usuarioId, e.empresaId, ev.target.value, token);
                  await recargarDetalleSuper(detalle.usuarioId);
                  await cargar();
                }}
              >
                <option value="Usuario">Usuario de la empresa</option>
                <option value="EmpresaAdmin">Admin de la empresa</option>
              </select>
              <span style={{ fontSize: 11, color: e.activo ? "#27ae60" : "#aaa", whiteSpace: "nowrap" as const }}>
                {e.activo ? "activo" : "inactivo"}
              </span>
            </div>
          ))}

          {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
        </div>
      )}

      {/* PANEL: empresas del usuario (vista de grupo) */}
      {modoPanel === "empresasDeGrupo" && detalle && usuarioSeleccionado && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Empresas — {usuarioSeleccionado.nombre}</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          {empresasDelGrupoDisponibles.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <select style={{ ...styles.campoInput, flex: 1, minWidth: 140 }}
                value={nuevaEmpresa.empresaId}
                onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, empresaId: Number(e.target.value) })}>
                <option value={0}>— Elegir empresa —</option>
                {empresasDelGrupoDisponibles.map(e => (
                  <option key={e.empresaId} value={e.empresaId}>{e.nombre}</option>
                ))}
              </select>
              <select style={{ ...styles.campoInput, width: 120 }}
                value={nuevaEmpresa.rol}
                onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, rol: e.target.value })}>
                <option value="Usuario">Usuario</option>
                <option value="EmpresaAdmin">EmpresaAdmin</option>
              </select>
              <label style={styles.checkboxInline}>
                <input type="checkbox" checked={nuevaEmpresa.activo}
                  onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, activo: e.target.checked })} />
                Activo
              </label>
              <button style={styles.btnNuevo} onClick={handleAgregarEmpresa}>+</button>
            </div>
          )}

          {empresasDelGrupoAsignadas.length === 0 && <div style={styles.vacio}>Sin empresas asignadas</div>}
          {empresasDelGrupoAsignadas.map(e => (
            <div key={e.empresaId} style={styles.filaAsignacion}>
              <span style={{ flex: 1 }}>{e.nombre}</span>
              <select style={{ ...styles.campoInput, width: 120 }}
                value={e.rol}
                onChange={ev => handleCambiarRolEmpresaEnPanel(e.empresaId, ev.target.value)}>
                <option value="Usuario">Usuario</option>
                <option value="EmpresaAdmin">EmpresaAdmin</option>
              </select>
              <button style={styles.btnAccion} onClick={() => handleToggleEmpresaEnPanel(e.empresaId, !e.activo, e.nombre)}>
                {e.activo ? "Desactivar" : "Activar"}
              </button>
              <button style={{ ...styles.btnAccion, color: "#e74c3c", border: "1px solid #e74c3c" }}
                onClick={() => handleQuitarEmpresa(e.empresaId)}>✕</button>
            </div>
          ))}

          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
        </div>
      )}

      {/* PANEL: agregar usuario existente (exclusivo Superusuario) */}
      {modoPanel === "agregarExistente" && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>
              Agregar usuario existente {modo === "grupo" ? "al grupo" : "a la empresa"}
            </span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input style={{ ...styles.campoInput, flex: 1 }}
              placeholder="Buscar por nombre o email..."
              value={busquedaExistente}
              onChange={e => setBusquedaExistente(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleBuscarExistente()}
            />
            <button style={styles.btnNuevo} onClick={handleBuscarExistente} disabled={buscandoExistente}>
              {buscandoExistente ? "..." : "Buscar"}
            </button>
          </div>

          {busquedaExistente && !buscandoExistente && resultadosBusqueda.length === 0 && (
            <div style={styles.vacio}>Sin resultados</div>
          )}

          {resultadosBusqueda.map(u => {
            const rolDefault = modo === "grupo" ? "User" : "Usuario";
            const cfg = asignacionForm[u.usuarioId] || { rol: rolDefault, activo: false };
            return (
              <div key={u.usuarioId} style={styles.filaBusqueda}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{u.nombre}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{u.email}</div>
                </div>
                <select style={{ ...styles.campoInput, width: 110 }}
                  value={cfg.rol}
                  onChange={e => setAsignacionForm({ ...asignacionForm, [u.usuarioId]: { ...cfg, rol: e.target.value } })}>
                  {modo === "grupo" ? (
                    <>
                      <option value="User">User</option>
                      <option value="GroupAdmin">GroupAdmin</option>
                    </>
                  ) : (
                    <>
                      <option value="Usuario">Usuario</option>
                      <option value="EmpresaAdmin">EmpresaAdmin</option>
                    </>
                  )}
                </select>
                <label style={styles.checkboxInline}>
                  <input type="checkbox" checked={cfg.activo}
                    onChange={e => setAsignacionForm({ ...asignacionForm, [u.usuarioId]: { ...cfg, activo: e.target.checked } })} />
                  Activo
                </label>
                <button style={styles.btnNuevo} onClick={() => handleAgregarExistente(u.usuarioId)}>+</button>
              </div>
            );
          })}

          {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f0f0f0" },
  titulo: { fontSize: 15, fontWeight: 700, color: "#1a1a2e" },
  btnNuevo: { padding: "8px 16px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnSecundario: { padding: "8px 16px", backgroundColor: "#fff", color: "#2B8FCC", border: "1px solid #2B8FCC", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  filtros: { display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid #f0f0f0" },
  segmented: { display: "flex", backgroundColor: "#f4f6f8", borderRadius: 8, padding: 2 },
  segItem: { padding: "6px 14px", border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "#888", borderRadius: 6, cursor: "pointer" },
  segItemActivo: { backgroundColor: "#fff", color: "#2B8FCC", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  buscador: { flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, outline: "none" },
  mensajeErrorGlobal: { backgroundColor: "#fff0f0", color: "#e74c3c", padding: "8px 20px", fontSize: 13 },
  encabezado: { display: "flex", alignItems: "center", padding: "8px 16px", backgroundColor: "#f4f6f8", borderBottom: "2px solid #e0e0e0" },
  colH: { fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: "0.5px", textTransform: "uppercase" as const, padding: "0 8px" },
  tabla: { overflowY: "auto" as const, maxHeight: "calc(100vh - 320px)" },
  fila: { display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f5f5f5", fontSize: 13 },
  col: { display: "flex", alignItems: "center", padding: "0 8px" },
  vacio: { padding: 40, textAlign: "center" as const, color: "#bbb", fontSize: 14 },
  btnAccion: { padding: "4px 10px", backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  miniSelect: { padding: "3px 6px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 },
  badgeSuper: { backgroundColor: "#fff4e0", color: "#cc8b00", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700, marginLeft: 6 },
  panel: { width: 380, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)", padding: 20, flexShrink: 0 },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  panelTitulo: { fontSize: 14, fontWeight: 700, color: "#1a1a2e" },
  panelCerrar: { background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "#aaa" },
  subTitulo: { fontSize: 12, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginTop: 20, marginBottom: 10, borderTop: "1px solid #f0f0f0", paddingTop: 14 },
  campo: { marginBottom: 12 },
  campoLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 4 },
  campoInput: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  campoCheckbox: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13, color: "#333" },
  checkboxInline: { display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#555", whiteSpace: "nowrap" as const },
  filaAsignacion: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 },
  filaBusqueda: { display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "1px solid #f5f5f5", flexWrap: "wrap" as const },
  nota: { fontSize: 12, color: "#888", backgroundColor: "#f8f9fa", padding: "8px 10px", borderRadius: 8, marginBottom: 12 },
  btnGuardar: { width: "100%", padding: 10, backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 8 },
  btnEliminar: { padding: "10px 16px", backgroundColor: "#fff0f0", color: "#e74c3c", border: "1px solid #f5c6c6", borderRadius: 8, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" as const },
  btnCancelar: { flex: 1, padding: 10, backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
};